import { BusinessObjectBase, LocalContext, ModelBase, ObservableCollection } from '@skylab-shared/core';
import { CalculationMethod, DiscountKind, DocumentPaymentListItemModel, ExchangeRateModel, JpkVatCodes, KsefDocumentStatus, KsefSendSubmissionMode, ProductModel, RateNP, SaleAdvanceAdjustmentDocumentModel, SaleAdvanceDocumentModel, SaleDocumentItemModel, SaleDocumentKind, SaleDocumentModel, SalePatternModel } from '@skylab-shared/dataModel';
import { Dictionary } from '@skylab-shared/types';
import { Calc, MidpointRounding, dateDiffInDays, head } from '@skylab-shared/utils';
import { ContextKeys } from '../../common/context-keys';
import { DocumentWithExchangeRate, sortVatRates } from '../../common/document-utils';
import { JpkVatProcedures } from '../../common/jpk-vat-procedures';
import { CommunityTransactionForSaleQuery, SaleCommunityTransactionItem, SaleCommunityTransactionSymbol } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParametersQuery } from '../../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery } from '../../currency/queries/currency.query-base';
import { ProductRepositoryContract } from '../../shared-business-temporary-contracts';
import { UnitQuery } from '../../unit/queries/unit.query-base';
import { VatRateForSaleBaseQuery } from '../../vat/queries/vat-rate-for-sale-base.query-base';
import { VatRateForSaleWstoQuery } from '../../vat/queries/vat-rate-for-sale-wsto.query-base';
import { SaleJpkVatGroupCollectionItem } from '../queries/sale-jpk-vat-group.query-base';

export interface FillDocumentItemResult {
    vatRateId: {
        previous: number;
        current: number;
        wstoVatRateOnProduct?: number | null;
    };
}
//TODO:wrapper synchronize

export const SaleItemQuantityPrecision = 6;

export interface DocumentForItemWrapper extends ModelBase {
    readonly calculationMethod: CalculationMethod;
    readonly communityTransactionId: number;
    readonly currencyId: number;
    readonly issuedByVatPayer: boolean;
    readonly wstoConsumerCountryId?: number | null;
    readonly jpkVatGroups: string[];
    readonly ksefDocumentStatus?: KsefDocumentStatus;
    readonly exchangeRate: ExchangeRateModel;
}

export async function fillDocumentItemAsync(
    item: SaleDocumentItemModel,
    productId: number,
    name: string,
    document: DocumentForItemWrapper,
    localContext: LocalContext,
    productRepository: ProductRepositoryContract,
    options = { isSystemCurrencyPriceDefault: true }
): Promise<FillDocumentItemResult> {
    const isWstoOssDocument = localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'WSTO-OSS' })?.id === document.communityTransactionId;

    item.discountKind = DiscountKind.Percentage;

    const product = productId ? await localContext.getAsync(ContextKeys.product(productId), () => productRepository.findAsync(productId)) : new ProductModel();
    let wstoVatRateOnProduct: number | null = null;

    item.symbol = product.symbol;
    if (productId) item.productId = productId;

    //#region vat rates
    if (isWstoOssDocument) {
        if (document.wstoConsumerCountryId) {
            const countryVatRates = localContext.filterPreloaded(VatRateForSaleWstoQuery, { countryId: document.wstoConsumerCountryId, active: true });
            const vatRateOnProduct = countryVatRates.find(elem => elem.id === product.ossVatRates.find(elem => elem.countryId === document.wstoConsumerCountryId)?.vatRateId);

            if (vatRateOnProduct?.active) {
                item.vatRateId = wstoVatRateOnProduct = vatRateOnProduct.id;
            } else if (countryVatRates?.length) {
                const defaultVatRateId = countryVatRates.find(x => x.default)?.id;
                item.vatRateId = defaultVatRateId || head(sortVatRates(countryVatRates))?.id || 0;
            }
        }
    } else {
        const vatRates = await localContext.refresh(VatRateForSaleBaseQuery);
        if (!productId) {
            const vatRate = document.issuedByVatPayer 
                ? (vatRates.find(vatRate => vatRate.default) ?? vatRates[0])
                : (vatRates.find(vatRate => vatRate.exempt) ?? vatRates[0]);
            if (vatRate) {
                product.vatRateId = vatRate.id;
            }
        }

        const availableVatRates = vatRates.filter(v => v.communityTransactions.indexOf(document.communityTransactionId) > -1);
        const defaultVatRateId = localContext.firstPreloaded(CommunityTransactionForSaleQuery, { id: document.communityTransactionId })?.defaultVatRateId
            || availableVatRates.find(vatRate => vatRate.default)?.id;

        const exemptVatRate = vatRates.find(v => v.exempt);
        const exemptVatRateId = exemptVatRate ? exemptVatRate.id : 0;
        const fallbackVatRateId = defaultVatRateId ?? exemptVatRateId;

        item.vatRateId = document.issuedByVatPayer
            ? ((product.vatRateId && availableVatRates.some(v => v.id === product.vatRateId)) ? product.vatRateId : fallbackVatRateId)
            : exemptVatRateId;
    }
    //#endregion

    const defaultUnit = await localContext.firstAsync(UnitQuery, { default: true });
    item.unitId = product.unitId || defaultUnit?.id || 0;
    item.jpkVatGroups = product.jpkVatGroups ? [...product.jpkVatGroups] : [];
    item.jpkVatProcedure = document.jpkVatGroups.find(elem => JpkVatProcedures.includes(elem as JpkVatCodes)) || null as any;
    item.cnCode = product.cnCode;
    item.barCode = product.barCode;
    item.productName = name;
    item.pkwiu = product.pkwiu;
    item.quantity = 1;

    item.includesInventoryControl = product.inventoryControl;
    item.inventoryLevel = product.inventoryControl ? product.inventoryLevel ?? 0 : null;

    const companyParams = localContext.getPreloaded(CompanyParametersQuery);
    const systemCurrency = companyParams?.systemCurrency;
    const firstCurrency = localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
    const systemCurrencyId = firstCurrency?.id || 0;
    const price = product.prices.find(elem => elem.currencyId === document.currencyId)
        || (options.isSystemCurrencyPriceDefault ? product.prices.find(elem => elem.currencyId === systemCurrencyId) : null);

    if (document.calculationMethod === CalculationMethod.Net) {
        item.price_netBeforeDiscount = price?.net ?? 0;
    } else if (document.calculationMethod === CalculationMethod.Gross || document.calculationMethod === CalculationMethod.Margin) {
        item.price_grossBeforeDiscount = price?.gross ?? 0;
    }

    return {
        vatRateId: {
            current: item.vatRateId,
            previous: (productId && product.vatRateId) || 0,
            wstoVatRateOnProduct
        }
    };
}

export function rewritePayments(target: ObservableCollection<DocumentPaymentListItemModel>, source: DocumentPaymentListItemModel[]) {
    target.clear();
    source.forEach(item => {
        const payment = new DocumentPaymentListItemModel();
        target.push(payment);
        payment.value = item.value;
        payment.date = item.date;
        payment.onDocument = item.onDocument;
        payment.id = item.id;
    });
}

export async function refreshSaleDocumentItemPriceAsync(item: SaleDocumentItemModel, bo: BusinessObjectBase<DocumentWithExchangeRate>, productRepository: ProductRepositoryContract, options = { isSystemCurrencyPriceDefault: true }): Promise<void> {
    const productId = item.productId;
    if (!productId)
        return;

    const currency = bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: bo.model.currencyId });
    const precision = currency?.precision ?? 2;
    const companyParams = bo.localContext.getPreloaded(CompanyParametersQuery);
    const systemCurrency = companyParams?.systemCurrency;
    const systemCurrencyId = bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })?.id || 0;

    const multiplier = (bo.model.currencyId !== systemCurrencyId) && bo.model.exchangeRate ? (bo.model.exchangeRate.rate || 1) / (bo.model.exchangeRate.units || 1) : 1;
    const product = await bo.localContext.getAsync(ContextKeys.product(productId), () => productRepository.findAsync(productId));

    const price = product.prices.find(elem => elem.currencyId === bo.model.currencyId);
    const priceBaseCurrency = product.prices.find(elem => elem.currencyId === systemCurrencyId);

    switch (bo.model.calculationMethod) {
        case CalculationMethod.Net: {
            const newPrice = price?.net ?? (options.isSystemCurrencyPriceDefault ? ((priceBaseCurrency?.net ?? 0) / multiplier) : 0);
            item.price_netBeforeDiscount = Calc.round(newPrice, precision, MidpointRounding.AwayFromZero);
        } break;

        case CalculationMethod.Gross:
        case CalculationMethod.Margin: {
            const newPrice = price?.gross ?? (options.isSystemCurrencyPriceDefault ? ((priceBaseCurrency?.gross ?? 0) / multiplier) : 0);
            item.price_grossBeforeDiscount = Calc.round(newPrice, precision, MidpointRounding.AwayFromZero);
        } break;
    }

    if (item.discountKind === DiscountKind.Quota) {
        const newDiscountValue = item.discountValue / multiplier;
        item.discountValue = Calc.round(newDiscountValue, precision, MidpointRounding.AwayFromZero);
    }
}

export function findDocumentAdjustmentKindForDocument(document: SaleDocumentModel): SaleDocumentKind {
    const kind = (<Dictionary<SaleDocumentKind>>{
        [SaleDocumentKind.Invoice]: SaleDocumentKind.InvoiceAdjustment,
        [SaleDocumentKind.InvoiceAdjustment]: SaleDocumentKind.InvoiceAdjustment,
        [SaleDocumentKind.AdvanceInvoice]: SaleDocumentKind.InvoiceAdjustment,
        [SaleDocumentKind.AdvanceInvoiceAdjustment]: SaleDocumentKind.InvoiceAdjustment,
        [SaleDocumentKind.Receipt]: SaleDocumentKind.ReceiptAdjustment,
        [SaleDocumentKind.ReceiptAdjustment]: SaleDocumentKind.ReceiptAdjustment,
        [SaleDocumentKind.VatMarginInvoice]: SaleDocumentKind.VatMarginInvoiceAdjustment,
        [SaleDocumentKind.VatMarginInvoiceAdjustment]: SaleDocumentKind.VatMarginInvoiceAdjustment
    })[document.kind];

    if (kind) return kind;

    throw new Error(`There is no adjustment kind for the given sale document kind (${document.kind}).`);
}

const communityTransactionOrder: SaleCommunityTransactionSymbol[] = [
    'SK',
    'OO',
    'WDT',
    'ET',
    'ŚUPTK',
    'DPTK',
    'WSTO-OSS',
    'WSTO-SK'
];

export function sortCommunityTransactions(array: SaleCommunityTransactionItem[]): SaleCommunityTransactionItem[] {
    return array.sort((item1, item2) => communityTransactionOrder.indexOf(<SaleCommunityTransactionSymbol>item1.symbol) - communityTransactionOrder.indexOf(<SaleCommunityTransactionSymbol>item2.symbol));
}

export function filterCommunityTransactionsFromOO(communityTransactions: SaleCommunityTransactionItem[], communityTransactionId: number): SaleCommunityTransactionItem[] {
    const isOoCommunityTransaction = communityTransactions.find(x => x.id === communityTransactionId)?.symbol === 'OO';
    return isOoCommunityTransaction ? communityTransactions : communityTransactions.filter(x => x.symbol !== 'OO');
}

export function isAdjustment(kind: SaleDocumentKind): boolean {
    return (
        kind === SaleDocumentKind.InvoiceAdjustment ||
        kind === SaleDocumentKind.ReceiptAdjustment ||
        kind === SaleDocumentKind.VatMarginInvoiceAdjustment) ||
        kind === SaleDocumentKind.AdvanceInvoiceAdjustment;
}

export const CommunityTransactionsForReceipt: SaleCommunityTransactionSymbol[] = ['SK', 'WSTO-OSS', 'WSTO-SK'];

export function isWstoTransaction(transactions: SaleCommunityTransactionItem[], transactionId: number): boolean {
    const wstoTransactions: SaleCommunityTransactionSymbol[] = ['WSTO-OSS', 'WSTO-SK'];
    return !!transactions.filter(elem => wstoTransactions.includes(elem.symbol)).find(elem => elem.id === transactionId);
}

export function getActiveJpkVatGroups(collection: SaleJpkVatGroupCollectionItem[], date: Date): SaleJpkVatGroupCollectionItem[] {
    return collection.filter(x => ((!x.activeFrom || dateDiffInDays(x.activeFrom, date) <= 0) && (!x.activeTo || dateDiffInDays(x.activeTo, date) >= 0)));
}

export function isJpkGroupActive(groups: SaleJpkVatGroupCollectionItem[], code: JpkVatCodes, date: Date): boolean {
    const activeGroups = getActiveJpkVatGroups(groups, date);
    return activeGroups.some(elem => elem.code === code);
}

export const isAdvance = (model: ModelBase & { kind: SaleDocumentKind; }): model is SaleAdvanceDocumentModel => model.kind === SaleDocumentKind.AdvanceInvoice;
export const isAdvanceAdjustment = (model: ModelBase & { kind: SaleDocumentKind; }): model is SaleAdvanceAdjustmentDocumentModel => model.kind === SaleDocumentKind.AdvanceInvoiceAdjustment;

export const isAdvanceModel = (model: ModelBase): model is SaleAdvanceDocumentModel => model instanceof SaleAdvanceDocumentModel;
export const isSalePatternModel = (model: ModelBase): model is SalePatternModel => model instanceof SalePatternModel;

export const isKsefDocumentType = (kind: SaleDocumentKind) => ![SaleDocumentKind.ProForma, SaleDocumentKind.Receipt, SaleDocumentKind.ReceiptAdjustment].includes(kind);

export const isKsefDocumentSendWithSuccess = (model: { ksefDocumentStatus: KsefDocumentStatus }) => model.ksefDocumentStatus === KsefDocumentStatus.Registered;
export const isKsefDocumentSendWithError = (model: { ksefDocumentStatus: KsefDocumentStatus }) => [KsefDocumentStatus.SendingError, KsefDocumentStatus.Rejected].includes(model.ksefDocumentStatus);
export const isKsefDocumentSendPending = (model: { ksefDocumentStatus: KsefDocumentStatus }) => model.ksefDocumentStatus === KsefDocumentStatus.InProgress;
export const isKsefDocumentToSend = (model: { ksefDocumentStatus: KsefDocumentStatus }) => [
    KsefDocumentStatus.ToSend, KsefDocumentStatus.InvalidSchema, KsefDocumentStatus.SendingError, KsefDocumentStatus.Rejected
].includes(model.ksefDocumentStatus);
export const isDocumentToKsef = (model: { ksefSubmissionMode: KsefSendSubmissionMode}): boolean => {
    if (!model?.ksefSubmissionMode) return false;
    return model.ksefSubmissionMode !== 'None';
}
export const isDocumentToKsefOnlineMode = (model: { ksefSubmissionMode: KsefSendSubmissionMode}): boolean => model.ksefSubmissionMode === 'Online';
export type IKsefDeadlineDateCalculator = {
    calculate(ksefSubmissionMode: KsefSendSubmissionMode, issueDate: Date): Date | null;
}

export const extractKsefXmlFileNameFromUrl = (url: string | null) => {
    if (!url) return 'imported_file.xml'
    const pathname = new URL(url).pathname;
    return decodeURIComponent(pathname.split('/').pop() ?? '');
}

type DefaultNpValues = {
    reverseCharge: boolean;
    rateNP: RateNP | null;
}
export const getDefaultNPValues = (symbol: SaleCommunityTransactionSymbol) => {
    const defaultValues: Partial<Record<SaleCommunityTransactionSymbol, DefaultNpValues>> = {
        ['ŚUPTK']: { reverseCharge: true, rateNP: RateNP.II },
        ['DPTK']: { reverseCharge: true, rateNP: RateNP.I }
    }

    return defaultValues[symbol] ?? { reverseCharge: null, rateNP: null };
}